/*
 * ILS — TikTok browser ecommerce events (TASK-5343, content_id TASK-5388).
 *
 * ── WHAT TASK-5388 CHANGED, IN ONE LINE ────────────────────────────────────
 * content_id is now the Shopify PRODUCT id with content_type 'product_group',
 * not the variant id with 'product', because the TikTok catalog this store
 * publishes is now one row per product. Nothing else moved: same pixel, same
 * three events, same triggers, same consent rule, same values. See the long
 * comment on `content()` for the mechanism and the TikTok citation.
 *
 * DEPLOYED AS the live theme asset `assets/ils-tiktok-events.js` on
 * Dawn - Production (199402717525) and Dawn - Development (199402815829).
 * Loaded by `snippets/ils-tiktok-events.liquid`, which is rendered from one
 * line in layout/theme.liquid and which hands us window.ILS_TTQ (the Liquid-
 * side page facts: pixel id, presentment currency, template, product+variants).
 *
 * ── WHAT THIS IS AND IS NOT ────────────────────────────────────────────────
 * This is the BROWSER sender for the three storefront funnel events. It sends
 * nothing server-side and does not touch the Pageview the theme's base
 * snippet already fires.
 *
 *   ViewContent      product page render (the selected variant)
 *   AddToCart        a /cart/add that actually succeeded
 *   InitiateCheckout the REAL hand-off to Shopify checkout, i.e. Next on
 *                    /pages/delivery-selector
 *
 * The fourth event, CompletePayment, physically cannot be sent from here:
 * Shopify's hosted checkout and thank-you page render none of the theme. It
 * lives in assets/tiktok/ils-tiktok-custom-pixel-checkout.js and needs one
 * operator paste into Settings → Customer events. The split is deliberate and
 * is what keeps the two senders from ever reporting the same event twice —
 * see reports/TASK-5343_tiktok_browser_pixel_production_2026-10-09.md.
 *
 * ── WHY THE FUNNEL STEP IS `Next` AND NOT THE CART BUTTON ──────────────────
 * Since TASK-3340 the cart's own Checkout button does NOT go to Shopify: it is
 * gated (`[data-ils-checkout-gate]`) and redirects to /pages/delivery-selector.
 * The real funnel is cart → country → delivery → Shopify checkout, and the
 * control that hands off to Shopify is the delivery step's
 * `<button data-ils-next>` (custom-delivery-selector.js, TASK-4945 INSTANT_NEXT
 * path). Firing InitiateCheckout on the cart button would count every shopper
 * who merely opened the country step and would inflate the funnel by exactly
 * the drop-off this store is trying to measure. TASK-5104 proved the same
 * point for the first-party tracker's own checkout_started; this file reuses
 * that trigger.
 *
 * `pointerdown` is the primary trigger with `click` as a latched backstop. The
 * Next handler navigates through location.assign and awaits nothing, so a
 * request started at click time races the unload; pointerdown buys the pixel
 * the whole press. The shared latch means the pair can only produce one event.
 *
 * ── ONE SENDER PER PIXEL ───────────────────────────────────────────────────
 * The WeTracked app's script tag loads the RETIRED pixel D209AQRC77U59CPITGDG
 * onto the same window.ttq. With two pixels registered, a bare ttq.track()
 * reports to BOTH. Everything here therefore goes through
 * ttq.instance(PIXEL_ID).track(...). We cannot suppress WeTracked's own extra
 * ttq.page() — that is an operator change in WeTracked's dashboard — but no
 * ecommerce event of ours reaches the retired pixel.
 *
 * Fail-silent throughout: tracking must never break a storefront page.
 */
(function () {
  'use strict';

  var CFG = (window && window.ILS_TTQ) || {};
  var PIXEL_ID = CFG.pixel_id;
  if (!PIXEL_ID) return;

  /* Gift lines are added and removed by custom-gift-logic.js without the
     shopper doing anything, so they are not shopper intent and are excluded —
     the same rule and the same ids as ils-tracker.js (GIFT_VARIANT_BY_GRAMS in
     custom-gift-logic.js). All five share SKU DF021F. */
  var GIFT_SKU = 'DF021F';
  var GIFT_VARIANT_SET = {
    49820818047317: 1, 49820818080085: 1, 49820818112853: 1,
    52303207137621: 1, 52303207170389: 1
  };

  var sent = [];
  CFG.sent = sent; // breadcrumb for the DC panel and for TASK-5343's live checks

  function isGift(vid, sku) {
    try {
      if (sku && String(sku).toUpperCase() === GIFT_SKU) return true;
      var n = (typeof vid === 'number') ? vid : parseInt(vid, 10);
      return GIFT_VARIANT_SET[n] === 1;
    } catch (e) { return false; }
  }

  function currency() {
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active;
    } catch (e) {}
    return CFG.currency || null;
  }

  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return null;
    return Math.round(n) / 100;
  }

  function rid() {
    try {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    } catch (e) {}
    return Math.random().toString(36).slice(2, 14);
  }

  /* ── consent ───────────────────────────────────────────────────────────────
     The owner removed the consent banner in TASK-2553 and the theme's base
     TikTok snippet has fired Pageview unconditionally ever since, so "no
     decision recorded" cannot mean "do not track" here without silently
     disabling the pixel this task exists to fix. The rule is therefore: an
     EXPLICIT refusal of marketing blocks our events, the absence of a decision
     does not. Both channels the store actually has are honoured — our own
     localStorage.ils_consent (ils-consent.js) and Shopify's Customer Privacy
     API, which ils-consent.js mirrors into. shouldShowBanner() === true means
     Shopify is still waiting for an answer, which is not a refusal.
     We deliberately do NOT call ttq.holdConsent(): that would also silence the
     base snippet's Pageview, which is pre-existing behaviour and not ours to
     change under this task. */
  function marketingRefused() {
    try {
      var raw = localStorage.getItem('ils_consent');
      if (raw) {
        var c = JSON.parse(raw);
        if (c && c.marketing === false) return true;
      }
    } catch (e) {}
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function' && cp.marketingAllowed() === false) {
        var undecided = (typeof cp.shouldShowBanner === 'function') && cp.shouldShowBanner() === true;
        if (!undecided) return true;
      }
    } catch (e) {}
    return false;
  }

  /* ── transport ─────────────────────────────────────────────────────────── */
  function emit(eventName, eventId, properties) {
    try {
      if (marketingRefused()) return false;
      var ttq = window.ttq;
      if (!ttq || typeof ttq.instance !== 'function') return false;
      ttq.instance(PIXEL_ID).track(eventName, properties, { event_id: eventId });
      sent.push({ event: eventName, event_id: eventId, properties: properties, at: Date.now() });
      return true;
    } catch (e) { return false; }
  }

  /* ── content_id: the PRODUCT GROUP, not the variant (TASK-5388) ──────────
     This changed, and the reason is the catalog it has to match.

     It used to be the VARIANT id, because the catalog
     (https://feeds.ilovesnacks.eu/feeds/tiktok/{locale}.csv) carried every
     sellable variant: 940 rows for RU, one per pack, each with its own real
     price. That catalog let TikTok choose which of a product's five prices to
     show — including in Automatic Enhancements → Product Display Settings,
     whose price sticker has no per-ad-group setting — so the same product
     advertised 4.99 EUR in one impression and 54.99 EUR in the next. TASK-5388
     fixes that by shrinking the catalog to ONE row per product, the cheapest
     pack a shopper can actually buy. 705 of 940 variant ids therefore leave the
     catalog, and TikTok silently drops a content_id it has never seen, so
     keeping variant ids here would have unmatched every event on a pack that
     is not the representative.

     TikTok's own parameter reference gives the alternative explicitly:
     "Value must be set to 'product' or 'product_group'. If you use sku_id as
     'content_ids', use 'product'; if you use item_group_id as 'content_ids',
     use 'product_group'."
     (https://ads.tiktok.com/help/article/about-parameters)

     `item_group_id` in our catalog IS the Shopify numeric PRODUCT id — the same
     number Liquid already hands us as `CFG.product.id` and `/cart.js` returns as
     `product_id`. So this sends the product id with content_type
     'product_group'. Two properties make the switch safe in either order
     relative to the catalog change:

       * those group ids were ALREADY in the 940-row catalog TikTok accepted
         (one `item_group_id` column value per row), and they are unchanged in
         the one-row catalog — only the row count shrank. So events match
         before, during and after the catalog refresh.
       * nothing about the real event changes: `value`, `price`, `quantity` and
         `currency` still come from the variant the shopper actually chose. Only
         the key the event is ATTRIBUTED on moved up one level.

     Meta and Google are untouched: this file is the TikTok sender only, and it
     emits through ttq.instance(PIXEL_ID) exclusively.

     FALLBACK: if a product id is somehow absent we fall back to the variant id
     with content_type 'product' rather than dropping the event. That row is no
     longer in the catalog, so it will not match — but an unmatched event is
     strictly better than a missing one, and it keeps this function total. */
  function content(fields) {
    var group = (fields.product_id === null || fields.product_id === undefined || fields.product_id === '')
      ? null
      : String(fields.product_id);
    var c = {
      content_id: group || String(fields.variant_id),
      content_type: group ? 'product_group' : 'product',
      quantity: fields.quantity || 1
    };
    if (fields.price != null) c.price = fields.price;
    if (fields.title) c.content_name = fields.title;
    if (fields.vendor) c.brand = fields.vendor;
    if (fields.type) c.content_category = fields.type;
    return c;
  }

  /* The EVENT-level content_type must agree with what is in `contents`. It is
     'product_group' only when every entry is a group; one fallback entry makes
     the whole event 'product', which is the honest description of a payload
     that mixes the two rather than a claim TikTok would have to reconcile. */
  function contentTypeOf(contents) {
    for (var i = 0; i < contents.length; i++) {
      if (contents[i].content_type !== 'product_group') return 'product';
    }
    return contents.length ? 'product_group' : 'product';
  }

  /* ── ViewContent ───────────────────────────────────────────────────────── */
  function selectedVariant() {
    var p = CFG.product;
    if (!p || !p.variants || !p.variants.length) return null;
    var wanted = null;
    try {
      var m = /[?&]variant=(\d+)/.exec(window.location.search);
      if (m) wanted = m[1];
    } catch (e) {}
    if (!wanted && p.selected_variant_id) wanted = String(p.selected_variant_id);
    for (var i = 0; i < p.variants.length; i++) {
      if (String(p.variants[i].id) === String(wanted)) return p.variants[i];
    }
    return p.variants[0];
  }

  function fireViewContent() {
    var p = CFG.product;
    if (!p) return;
    var v = selectedVariant();
    if (!v) return;
    if (isGift(v.id, p.sku)) return;
    var item = content({
      product_id: p.id, variant_id: v.id, quantity: 1, price: v.price,
      title: p.title, vendor: p.vendor, type: p.type
    });
    /* `value` and `price` stay the SELECTED variant's price, not the product's
       cheapest. The shopper is looking at a specific pack and that is what the
       event is worth; only the attribution key moved to the product group. The
       event_id still carries the variant id, so a live check can still tell
       which pack produced the event. */
    emit('ViewContent', 'vc_' + v.id + '_' + Date.now() + '_' + rid(), {
      currency: currency(),
      value: v.price,
      content_type: contentTypeOf([item]),
      contents: [item]
    });
  }

  /* ── AddToCart ─────────────────────────────────────────────────────────── */
  function urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
      if (input && typeof input.toString === 'function') return input.toString();
    } catch (e) {}
    return '';
  }

  function handleAddResponse(resp) {
    try {
      if (!resp || !(resp.ok || (resp.status >= 200 && resp.status < 300))) return;
      resp.clone().json().then(function (data) {
        if (!data) return;
        /* /cart/add.js answers with the added line item, or with { items: [...] }
           when several were added (and when Dawn asks for `sections`). Same
           shape ils-tracker.js has read in production since TASK-2545. */
        var items = (data.items && data.items.length) ? data.items : [data];
        var contents = [];
        var value = 0;
        var cur = null;
        var firstVariant = null;
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (!it || !it.variant_id) continue;
          if (isGift(it.variant_id, it.sku)) continue;
          var qty = it.quantity || 1;
          var unitCents = (it.final_price != null) ? it.final_price : it.price;
          var lineCents = (it.final_line_price != null) ? it.final_line_price : unitCents * qty;
          var line = money(lineCents);
          if (line != null) value += line;
          if (!cur && it.currency) cur = it.currency;
          if (firstVariant === null) firstVariant = it.variant_id;
          /* `/cart/add.js` returns `product_id` alongside `variant_id` on every
             line item, which is the group id (TASK-5388). Lines are NOT merged
             by product: two packs of one product stay two entries with their own
             real unit prices and quantities, so the event's `value` is unchanged
             from before this task. TikTok tolerates a repeated content_id in
             `contents`; inventing a blended unit price to avoid one would not be
             a true price for either pack. */
          contents.push(content({
            product_id: it.product_id, variant_id: it.variant_id, quantity: qty, price: money(unitCents),
            title: it.product_title || it.title, vendor: it.vendor, type: it.product_type
          }));
        }
        if (!contents.length) return;
        /* The event id keys on the VARIANT, not on content_id, so it stays as
           unique as it was before the group switch - two packs of one product
           added in the same second must not collide. */
        emit('AddToCart', 'atc_' + firstVariant + '_' + Date.now() + '_' + rid(), {
          currency: cur || currency(),
          value: Math.round(value * 100) / 100,
          content_type: contentTypeOf(contents),
          contents: contents
        });
      }).catch(function () {});
    } catch (e) {}
  }

  /* window.fetch is already wrapped by custom-add-toast.js and again by
     ils-tracker.js. We capture whatever window.fetch is at install time and
     call it through .apply, read the body only via resp.clone(), never alter
     the response and never delay the call — the order-independent chaining
     rule from the TASK-2541 production-conflict audit. */
  function installFetchWrapper() {
    try {
      var prior = window.fetch;
      if (typeof prior !== 'function') return;
      window.fetch = function (input, init) {
        var p = prior.apply(this, arguments);
        try {
          var url = urlOf(input);
          if (url && /\/cart\/add(\.js)?(\?|$)/.test(url)) p.then(handleAddResponse).catch(function () {});
        } catch (e) {}
        return p;
      };
    } catch (e) {}
  }

  /* Non-AJAX <form action="/cart/add"> submits navigate, so the fetch wrapper
     never sees them. Dawn's <product-form> is AJAX and is skipped here or we
     would count it twice — identical rule to ils-tracker.js. */
  function bindCartFormFallback() {
    document.addEventListener('submit', function (ev) {
      try {
        var form = ev.target;
        if (!form || form.tagName !== 'FORM') return;
        if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
        if (form.closest && form.closest('product-form')) return;
        var idEl = form.querySelector('[name="id"]');
        var qtyEl = form.querySelector('[name="quantity"]');
        var vid = idEl ? parseInt(idEl.value, 10) : null;
        if (!vid || isGift(vid)) return;
        var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
        var price = null;
        var v = selectedVariant();
        if (v && String(v.id) === String(vid)) price = v.price;
        /* This path only fires on a product page, so Liquid's own
           `CFG.product.id` is the group id for it (TASK-5388). */
        var fallbackItem = content({
          product_id: CFG.product && CFG.product.id,
          variant_id: vid, quantity: qty, price: price,
          title: CFG.product && CFG.product.title,
          vendor: CFG.product && CFG.product.vendor,
          type: CFG.product && CFG.product.type
        });
        var props = {
          currency: currency(),
          content_type: contentTypeOf([fallbackItem]),
          contents: [fallbackItem]
        };
        if (price != null) props.value = Math.round(price * qty * 100) / 100;
        emit('AddToCart', 'atc_' + vid + '_' + Date.now() + '_' + rid(), props);
      } catch (e) {}
    }, true);
  }

  /* ── InitiateCheckout ──────────────────────────────────────────────────── */
  var cart = null;
  var checkoutFired = false;

  function readCart() {
    try {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { cart = c; })
        .catch(function () {});
    } catch (e) {}
  }

  function fireInitiateCheckout() {
    if (checkoutFired) return;
    if (!cart || !cart.items || !cart.items.length) return; // a confirmed-empty cart is not a hand-off
    checkoutFired = true;
    var contents = [];
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !it.variant_id || isGift(it.variant_id, it.sku)) continue;
      /* `/cart.js` line items carry `product_id` too (TASK-5388). */
      contents.push(content({
        product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity || 1,
        price: money(it.final_price != null ? it.final_price : it.price),
        title: it.product_title || it.title, vendor: it.vendor, type: it.product_type
      }));
    }
    /* The cart token keys the event id, so a shopper who returns through
       bfcache and presses Next again does not book a second InitiateCheckout
       for the same cart. One checkout entry per cart, deliberately on the
       under-reporting side. */
    var token = null;
    try { token = cart.token ? String(cart.token).split('?')[0] : null; } catch (e) {}
    emit('InitiateCheckout', token ? 'ic_' + token : 'ic_' + Date.now() + '_' + rid(), {
      currency: cart.currency || currency(),
      value: money(cart.total_price),
      content_type: contentTypeOf(contents),
      contents: contents
    });
  }

  function bindDeliveryNext() {
    if (CFG.template !== 'page.delivery-selector') return;
    readCart();
    var handler = function (ev) {
      try {
        var t = ev.target;
        if (!t || typeof t.closest !== 'function') return;
        var btn = t.closest('[data-ils-next]');
        if (!btn || btn.disabled) return;
        fireInitiateCheckout();
      } catch (e) {}
    };
    document.addEventListener('pointerdown', handler, true);
    document.addEventListener('click', handler, true);
  }

  /* ── init ──────────────────────────────────────────────────────────────── */
  function init() {
    try {
      installFetchWrapper();
      bindCartFormFallback();
      bindDeliveryNext();
      fireViewContent();
    } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
